import { useEffect, useState } from 'react'
import { BOOKS, BOOK_CATEGORIES, type BookCategory, type StoreBook } from '../data/books'
import { ArrowIcon } from './SiteShell'
import { sampleUrl, studioUrl } from '../data/books'

export type BookFilter = 'All' | BookCategory

export function CategoryTabs({ value, onChange, labelledBy }: { value: BookFilter; onChange: (category: BookFilter) => void; labelledBy?: string }) {
  return (
    <div className="category-tabs" role="group" aria-label={labelledBy ? undefined : 'Filter books by category'} aria-labelledby={labelledBy}>
      {BOOK_CATEGORIES.map(category => (
        <button type="button" className={category === value ? 'active' : ''} aria-pressed={category === value} onClick={() => onChange(category)} key={category}>{category}</button>
      ))}
    </div>
  )
}

export function BookCard({ book }: { book: StoreBook }) {
  const imageUrl = book.sampleAvailable ? sampleUrl(book.id) : studioUrl(book.id)
  return (
    <article className={`product-card product-card-${book.accent}`}>
      <a className="product-image" href={imageUrl} aria-label={book.sampleAvailable ? `Read a sample of ${book.title}` : `Personalize ${book.title}`}>
        <img src={book.image} alt={`${book.title} personalized book cover`} />
        {book.badge && !book.deliveryNote && <span className="product-badge">{book.badge}</span>}
        <span className="product-view">{book.sampleAvailable ? 'Read full sample' : 'See this book'} <ArrowIcon /></span>
      </a>
      <div className="product-info">
        {book.badge && book.deliveryNote && <span className="holiday-book-badge">{book.badge}</span>}
        <div className="product-title-row"><div><span>{book.category} · {book.age}</span><h3>{book.title}</h3></div></div>
        <p>{book.description}</p>
        {book.deliveryNote && <p className="holiday-delivery-note">{book.deliveryNote}</p>}
        <div className="product-meta"><span>{book.pageCount} total pages</span><span>Premium hardcover</span><span>Free shipping</span></div>
        {book.sampleAvailable && <a className="product-sample-link" href={sampleUrl(book.id)}>Read the complete sample <ArrowIcon /></a>}
        <a className="button button-full" href={studioUrl(book.id)}>Personalize this book <ArrowIcon /></a>
        <small className="preview-note">Create a preview free · Pay only when you order</small>
      </div>
    </article>
  )
}

export function BookGrid({ initialCategory = 'All', books = BOOKS, syncCategoryToUrl = false }: { initialCategory?: BookFilter; books?: StoreBook[]; syncCategoryToUrl?: boolean }) {
  const [category, setCategory] = useState<BookFilter>(initialCategory)
  const visibleBooks = category === 'All' ? books : books.filter(book => book.category === category)

  useEffect(() => {
    if (!syncCategoryToUrl) return
    const syncFromUrl = () => {
      const requested = new URLSearchParams(window.location.search).get('category')
      setCategory(BOOK_CATEGORIES.includes(requested as BookFilter) ? requested as BookFilter : 'All')
    }
    window.addEventListener('popstate', syncFromUrl)
    return () => window.removeEventListener('popstate', syncFromUrl)
  }, [syncCategoryToUrl])

  function chooseCategory(nextCategory: BookFilter) {
    if (nextCategory === category) return
    setCategory(nextCategory)
    if (!syncCategoryToUrl) return
    const url = new URL(window.location.href)
    if (nextCategory === 'All') url.searchParams.delete('category')
    else url.searchParams.set('category', nextCategory)
    window.history.pushState({}, '', url)
  }

  return (
    <>
      <CategoryTabs value={category} onChange={chooseCategory} />
      <div className={`product-grid ${visibleBooks.length === 1 ? 'product-grid-single' : ''}`} aria-live="polite">
        {visibleBooks.map(book => <BookCard book={book} key={book.id} />)}
      </div>
    </>
  )
}
